import { readCurrentTenantUuid } from '@/adapters/tenantStorage';
import { getCurrentUser } from '@/utils/currentMember';
import { BizAppConfig } from 'config/application.manifest';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';
import type { ApiRequestOptions } from './types/common';

const MEMBER_OPTIONS_STORAGE_PREFIX = 'project-center:member-options:v1';
const MEMBER_OPTIONS_CACHE_CHANGED_EVENT = 'member-options-cache-changed';
const EMPTY_MEMBER_OPTIONS: MemberOption[] = [];

type MemberListItem = {
  id?: string;
  memberUuid?: string;
  userUuid?: string;
  username?: string;
  nickname?: string;
  name?: string;
  realName?: string;
  photo?: string;
  avatar?: string;
  avatarUrl?: string;
  organizationUuid?: string;
  organizationName?: string;
  departmentUuid?: string;
  departmentName?: string;
  orgs?: Array<{
    nodeId?: string;
    orgId?: string;
    name?: string;
  }>;
};

export type MemberOption = {
  id: string;
  memberUuid?: string;
  userUuid?: string;
  memberName: string;
  avatar?: string;
  departmentUuid?: string;
  departmentName?: string;
  label: string;
};

type MemberOptionsCacheEntry = {
  updatedAt: number;
  items: MemberOption[];
};

type MemberDirectoryContext = {
  tenantUuid: string;
  organizationUuid: string;
  memberUuid: string;
};

const memberOptionsMemoryCache = new Map<string, MemberOptionsCacheEntry>();
const memberOptionsPendingPromiseByTenant = new Map<string, Promise<MemberOption[]>>();
const memberOptionsCacheListeners = new Set<() => void>();

const normalizeText = (value: unknown) => String(value ?? '').trim();

const resolveMemberDirectoryContext = (
  tenantUuid?: string | null,
  contextOverride?: Partial<MemberDirectoryContext>,
): MemberDirectoryContext => {
  const currentUser = getCurrentUser();
  const resolvedTenantUuid =
    normalizeText(contextOverride?.tenantUuid) ||
    normalizeText(tenantUuid) ||
    normalizeText(readCurrentTenantUuid()) ||
    normalizeText(currentUser?.tenantUuid);
  const organizationUuid =
    normalizeText(contextOverride?.organizationUuid) || normalizeText(currentUser?.organizationUuid);
  const memberUuid =
    normalizeText(contextOverride?.memberUuid) || normalizeText(currentUser?.memberUuid);

  if (!resolvedTenantUuid || !organizationUuid || !memberUuid) {
    throw new Error('当前会话缺少开放平台成员目录信息');
  }

  return { tenantUuid: resolvedTenantUuid, organizationUuid, memberUuid };
};

const getMemberOptionsStorageKey = (tenantUuid?: string | null) =>
  (() => {
    const context = resolveMemberDirectoryContext(tenantUuid);
    return `${MEMBER_OPTIONS_STORAGE_PREFIX}:${context.tenantUuid}:${context.organizationUuid}:${context.memberUuid}`;
  })();

const getOrganizationMemberUrl = (pageNo: number, pageSize: number) => {
  const serviceUrl = normalizeText(BizAppConfig.appOpenServiceUrl).replace(/\/+$/, '');
  const gatewayPath = normalizeText(BizAppConfig.appOpenApiGatewayPath).replace(/^\/+|\/+$/g, '');
  const categoryCode = normalizeText(
    BizAppConfig.appOrganizationAdminCategoryCode,
  ).replace(/^\/+|\/+$/g, '');
  if (!serviceUrl || !gatewayPath || !categoryCode) {
    throw new Error('开放平台成员目录配置不完整');
  }
  return `${serviceUrl}/${gatewayPath}/${categoryCode}/api/v1/members?pageNo=${pageNo}&pageSize=${pageSize}`;
};

const readStoredMemberOptions = (tenantUuid?: string | null): MemberOptionsCacheEntry | null => {
  const storageKey = getMemberOptionsStorageKey(tenantUuid);
  const cached = memberOptionsMemoryCache.get(storageKey);
  if (cached) {
    return cached;
  }

  try {
    const rawValue = localStorage.getItem(storageKey);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<MemberOptionsCacheEntry>;
    if (!Array.isArray(parsed.items)) {
      return null;
    }

    const entry: MemberOptionsCacheEntry = {
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
      items: parsed.items.filter((item): item is MemberOption => Boolean(item && item.id)),
    };
    memberOptionsMemoryCache.set(storageKey, entry);
    return entry;
  } catch {
    return null;
  }
};

const notifyMemberOptionsCacheChanged = () => {
  memberOptionsCacheListeners.forEach((listener) => listener());
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(MEMBER_OPTIONS_CACHE_CHANGED_EVENT));
  }
};

const writeStoredMemberOptions = (tenantUuid: string, items: MemberOption[]) => {
  const storageKey = getMemberOptionsStorageKey(tenantUuid);
  const entry: MemberOptionsCacheEntry = {
    updatedAt: Date.now(),
    items,
  };
  memberOptionsMemoryCache.set(storageKey, entry);

  try {
    localStorage.setItem(storageKey, JSON.stringify(entry));
  } catch {
    // ignore storage failures
  }

  notifyMemberOptionsCacheChanged();
};

const resolveMemberName = (member: MemberListItem): string => {
  return (
    member.name?.trim() ||
    member.realName?.trim() ||
    member.nickname?.trim() ||
    member.username?.trim() ||
    '未命名成员'
  );
};

const resolveDepartment = (member: MemberListItem) => {
  const firstOrg = member.orgs?.[0];

  return {
    departmentUuid:
      member.departmentUuid?.trim() ||
      member.organizationUuid?.trim() ||
      firstOrg?.orgId?.trim() ||
      firstOrg?.nodeId?.trim() ||
      undefined,
    departmentName:
      member.departmentName?.trim() ||
      member.organizationName?.trim() ||
      firstOrg?.name?.trim() ||
      undefined,
  };
};

const toMemberOption = (member: MemberListItem): MemberOption | null => {
  const id = member.memberUuid?.trim() || member.id?.trim() || '';

  if (!id) {
    return null;
  }

  const memberName = resolveMemberName(member);
  const { departmentUuid, departmentName } = resolveDepartment(member);
  const avatar = member.avatar?.trim() || member.avatarUrl?.trim() || member.photo?.trim() || undefined;

  return {
    id,
    memberUuid: member.memberUuid?.trim() || undefined,
    userUuid: member.userUuid?.trim() || undefined,
    memberName,
    avatar,
    departmentUuid,
    departmentName,
    label: departmentName ? `${memberName} / ${departmentName}` : memberName,
  };
};

const normalizeMemberOptions = (members: MemberListItem[]) => {
  const uniqueOptions = new Map<string, MemberOption>();

  members.forEach((member) => {
    const option = toMemberOption(member);
    if (!option || uniqueOptions.has(option.id)) {
      return;
    }
    uniqueOptions.set(option.id, option);
  });

  return Array.from(uniqueOptions.values());
};

export const subscribeMemberOptionsCache = (listener: () => void) => {
  memberOptionsCacheListeners.add(listener);

  return () => {
    memberOptionsCacheListeners.delete(listener);
  };
};

export const getMemberOptionsSnapshot = (tenantUuid?: string | null) => {
  try {
    return readStoredMemberOptions(tenantUuid)?.items || EMPTY_MEMBER_OPTIONS;
  } catch {
    return EMPTY_MEMBER_OPTIONS;
  }
};

export const clearMemberOptionsCache = (tenantUuid?: string | null) => {
  if (tenantUuid) {
    const keyPrefix = `${MEMBER_OPTIONS_STORAGE_PREFIX}:${normalizeText(tenantUuid)}:`;
    Array.from(memberOptionsMemoryCache.keys())
      .filter((key) => key.startsWith(keyPrefix))
      .forEach((key) => memberOptionsMemoryCache.delete(key));
    try {
      const keysToRemove: string[] = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (key?.startsWith(keyPrefix)) keysToRemove.push(key);
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // ignore storage failures
    }
  } else {
    memberOptionsMemoryCache.clear();
    try {
      const keysToRemove: string[] = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (key?.startsWith(MEMBER_OPTIONS_STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // ignore storage failures
    }
  }

  notifyMemberOptionsCacheChanged();
};

export const warmMemberOptionsCache = async (
  tenantUuid?: string | null,
  options?: ApiRequestOptions & {
    forceRefresh?: boolean;
    context?: Partial<MemberDirectoryContext>;
  },
) => {
  const context = resolveMemberDirectoryContext(tenantUuid, options?.context);
  const storageKey = getMemberOptionsStorageKey(context.tenantUuid);
  const cached = readStoredMemberOptions(context.tenantUuid);
  if (cached && !options?.forceRefresh) {
    return cached.items;
  }

  const pending = memberOptionsPendingPromiseByTenant.get(storageKey);
  if (pending) {
    return pending;
  }

  const pendingPromise = (async () => {
    const token = await fetchOpenPlatformToken(context);
    const members: MemberListItem[] = [];
    const pageSize = 100;
    let pageNo = 1;

    while (true) {
      const response = await fetch(getOrganizationMemberUrl(pageNo, pageSize), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token.accessToken}`,
          memberUuid: context.memberUuid,
          ...(options?.headers || {}),
        },
      });
      if (!response.ok) {
        throw new Error(`成员目录加载失败 (${response.status})`);
      }
      const payload = (await response.json()) as {
        list?: MemberListItem[];
        total?: number;
      };
      const items = Array.isArray(payload.list) ? payload.list : [];
      members.push(...items);
      const total = typeof payload.total === 'number' ? payload.total : members.length;
      if (!items.length || members.length >= total) break;
      pageNo += 1;
    }

    const result = normalizeMemberOptions(members);
    writeStoredMemberOptions(context.tenantUuid, result);
    return result;
  })();

  memberOptionsPendingPromiseByTenant.set(storageKey, pendingPromise);

  try {
    return await pendingPromise;
  } finally {
    memberOptionsPendingPromiseByTenant.delete(storageKey);
  }
};

export async function getMemberOptions(options?: ApiRequestOptions) {
  const tenantUuid = resolveMemberDirectoryContext().tenantUuid;
  const cached = readStoredMemberOptions(tenantUuid);
  if (cached) {
    return cached.items;
  }

  return warmMemberOptionsCache(tenantUuid, options);
}
